/**
 * Module 63 (WP-63.E4) — Surge: every closed powered gate opens until a block
 * leaves through one (Module 74; it was "for one move").
 *
 * Mirrors `rainbow-gates.ts` exactly in shape — `activation: 'immediate'`, a
 * board MODE the shared exit predicate reads through `ExitOptions.surge`, no
 * removal, no target — and differs in the one clause it lifts (power, never
 * colour — decision 6) and in how it ends (an exit through a gate only Surge
 * was holding open — see `model/surge.ts`).
 *
 * Refuses to activate when it would buy nothing: already surging, or no
 * powered gate is currently SHUT. A player pressing Surge on a board whose only
 * circuit is already lit would be paying for a mode that changes no answer,
 * and the bar's disabled state is the first lock against that; this is the
 * second, and the one that makes a double-press free rather than a purchase.
 */
import { BlockJamAudioId } from '../../constants/audio-ids';
import { gateIsOpen, poweredPlateIds } from '../../model/power';
import { activateSurge, endSurge, isSurgeActive } from '../../model/surge';
export const SURGE_ID = 'surge';
/** Is any powered gate shut right now? The only situation Surge can change. */
export function hasClosedPoweredGate(state) {
    const powered = poweredPlateIds(state.blocks, state.puzzle.plates);
    return state.puzzle.exits.some((e) => e.powered && !gateIsOpen(e, powered));
}
export const surgeEffect = {
    activation: 'immediate',
    /**
     * Module 91 — Master Key is paid when it is SPENT, like Wild Gates (Module
     * 90): opening it charges nothing, the exit that goes out through a gate only
     * it was holding open (`surgeSpentBy`) pays, and until then its price is held
     * and its button cancels for free. Setting these three is what switches on
     * the every-tap confirm card, the free open and the cancel in the plugin.
     */
    chargesWhenSpent: true,
    isOpen: isSurgeActive,
    close: endSurge,
    /**
     * Module 70 — the lock `surge.ts` claimed the bar already had, and it did not.
     *
     * The header below said "the bar's disabled state is the first lock against
     * that"; `hasClosedPoweredGate` was exported and nothing outside this file
     * called it, so the button sat bright and fully affordable on every board
     * without a powered circuit and its tap did nothing at all — silently, because
     * the refusal `activate` returns is discarded by the HUD's arm callback.
     *
     * Beginner is where that bites: Surge unlocks at puzzle 20, powered gates need
     * effective level 22, and Beginner's endless phase cycles back DOWN below 22 —
     * only 13 of its 26 puzzles from 20 on carry a powered gate, against 26 of 26
     * on both other tracks. Reported from Beginner puzzle 31. (Module 73 moved
     * Surge to puzzle 30 and puts a powered gate on 2 of every 3 endless Beginner
     * puzzles, so this seam now covers the 1-in-3 plain puzzle, not 11-long runs.)
     *
     * Same predicate as `activate`'s third guard, deliberately: the button's
     * enabled state and the effect's refusal must never be able to disagree.
     */
    availableOn: hasClosedPoweredGate,
    activate(state) {
        if (isSurgeActive(state))
            return null;
        if (state.blocks.length === 0)
            return null;
        if (!hasClosedPoweredGate(state))
            return null;
        return activateSurge(state);
    },
    /** Unreachable for an `'immediate'` effect — see `rainbow-gates.ts`. */
    targets() {
        return [];
    },
    /** Unreachable for an `'immediate'` effect. */
    apply() {
        return null;
    },
    /**
     * The gates opening IS the effect — the cue rides that paint. Module 140: a
     * key turns in each lock first, and the cue rides the first one's turn.
     */
    unlockFx: true,
    impactAudioId: BlockJamAudioId.POWERUP_SURGE,
};
